import { useTranslation } from 'react-i18next';
import Blade from '@/components/ui/Blade/Blade';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import donation from '@/assets/icons/donation.svg';
import Transaction from '@/models/Transaction';
import Debit from '@/models/Debit';
import styles from './AdditionalInfo/AdditionalInfo.module.css';
import own from './BankStandingOrdersBlade.module.scss';

interface BankStandingOrdersBladeProps {
  /** A projected `standingOrder` row of «תנועות עתידיות»: one collection date, every order due on it. */
  transaction: Transaction;
  handleXclick: () => void;
}

const orderName = (order: Debit): string => [order.firstName, order.lastName].filter(Boolean).join(' ').trim() || '—';

/**
 * HAT-1309: what a bank standing-order row of «תנועות עתידיות» stands for. The row sums every
 * order collected on one date; Druk (23.9.2026) asked that its click open the whole list, not a
 * part of it, so every order is listed — largest first, the way a coordinator checks a sum.
 */
const BankStandingOrdersBlade = ({ transaction, handleXclick }: BankStandingOrdersBladeProps) => {
  const { t, i18n } = useTranslation();
  const orders = [...(transaction.future?.standingOrders ?? [])].sort((a, b) => Math.abs(b.monthlyAmount) - Math.abs(a.monthlyAmount));
  const money = (value: number) => value.toLocaleString(i18n.language, { style: 'currency', currency: transaction.currency || 'ILS' });
  const date = new Date(transaction.date);
  const shifted = orders.some((order) => order.billingDayOfMonth !== date.getDate());

  const headerTop = <span className="body-lr-regular">{transaction.contactName}</span>;
  const headerBottom = (
    <>
      <span className={`${styles.amount} body-sm-medium text-(--font-tertiary) bg-(--surface-tertiary-low)`}>
        <img src={donation} alt="" />
        {money(transaction.amount)}
      </span>
      <span className="body-sm-regular">{Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)}</span>
    </>
  );

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="456px">
      {/* The transaction blade's tab band, over a body that fills the blade and scrolls only in the list. */}
      <Tabs defaultValue="orders" className={own.tabs} style={{ direction: 'inherit' }}>
        <TabsList>
          <TabsTrigger value="orders">{t('futureBankStandingOrdersTab')}</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className={own.content}>
          <div className={`${own.caption} body-sm-regular text-(--font-body-2)`}>
            <span>{t('futurePendingExecution')}</span>
            {shifted && <span>{t('futureBankStandingOrdersShifted')}</span>}
          </div>
          <ul className={`${own.list} custom-scrollbar`} aria-label={transaction.contactName}>
            {orders.map((order) => (
              <li key={order.donationId} className={own.order}>
                <span className={own.orderText}>
                  <span className="body-md-regular text-(--font-body-1)">{orderName(order)}</span>
                  <span className="body-sm-regular text-(--font-body-3)">
                    {[order.billingAccount, t('futureBankStandingOrderBillingDay', { day: order.billingDayOfMonth })].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className={`${own.amount} body-md-medium text-(--font-tertiary)`}>{money(Math.abs(order.monthlyAmount))}</span>
              </li>
            ))}
          </ul>
        </TabsContent>
      </Tabs>
    </Blade>
  );
};

export default BankStandingOrdersBlade;
